import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const NAV = [
  {
    label: "Services",
    children: [
      { to: "/services/cost-segregation", label: "Cost Segregation" },
      { to: "/services/rd-tax-credit", label: "R&D Tax Credit" },
    ],
  },
  { to: "/for-tax-professionals", label: "For CPAs" },
  { to: "/about", label: "About" },
  {
    label: "Knowledge Center",
    children: [
      { to: "/knowledge", label: "Articles" },
      { to: "/knowledge?tab=faqs", label: "FAQs" },
    ],
  },
  {
    label: "Calculator",
    children: [
      { to: "/calculators/cost-segregation", label: "Cost Segregation" },
      { to: "/calculators/rd-tax-credit", label: "R&D Calculator" },
    ],
  },
  { to: "/#contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On non-home pages we always use light navbar (page bg is parchment)
  const onLight = scrolled || !onHome;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        onLight
          ? "bg-ivory/95 backdrop-blur border-b border-ivory-400"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <div
            className={`h-9 w-9 flex items-center justify-center transition-colors ${
              onLight ? "bg-forest-900" : "bg-brass"
            }`}
          >
            <span
              className={`font-serif text-xl leading-none ${
                onLight ? "text-brass" : "text-forest-900"
              }`}
            >
              i
            </span>
          </div>
          <span
            className={`font-serif text-2xl tracking-tight ${
              onLight ? "text-forest-900" : "text-ivory"
            }`}
          >
            Incent<span className="text-brass">Tax</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenMenu(item.label)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <button
                  className={`text-sm transition-colors inline-flex items-center gap-1 ${
                    onLight ? "text-charcoal hover:text-forest-900" : "text-ivory/80 hover:text-ivory"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {item.label}
                  <ChevronDown size={14} />
                </button>
                {openMenu === item.label && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3">
                    <div className="bg-white border border-ivory-400 shadow-md min-w-[220px] py-2">
                      {item.children.map((c) =>
                        c.to.includes("#") ? (
                          <a
                            key={c.to}
                            href={c.to}
                            onClick={() => setOpenMenu(null)}
                            className="block px-5 py-3 text-charcoal hover:bg-ivory-200 hover:text-forest-900 text-sm"
                            data-testid={`nav-sub-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          >
                            {c.label}
                          </a>
                        ) : (
                          <Link
                            key={c.to}
                            to={c.to}
                            onClick={() => setOpenMenu(null)}
                            className="block px-5 py-3 text-charcoal hover:bg-ivory-200 hover:text-forest-900 text-sm"
                            data-testid={`nav-sub-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          >
                            {c.label}
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : item.to.startsWith("/#") ? (
              <a
                key={item.to}
                href={item.to}
                className={`text-sm transition-colors ${
                  onLight ? "text-charcoal hover:text-forest-900" : "text-ivory/80 hover:text-ivory"
                }`}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `text-sm transition-colors ${
                    onLight ? "text-charcoal hover:text-forest-900" : "text-ivory/80 hover:text-ivory"
                  } ${isActive ? "!text-brass" : ""}`
                }
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="/#contact"
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              onLight
                ? "bg-brass hover:bg-brass-500 text-forest-900"
                : "bg-brass hover:bg-brass-500 text-forest-900"
            }`}
            data-testid="nav-cta-consult"
          >
            Free Consultation
          </a>
        </div>

        <button
          aria-label="Menu"
          className={`lg:hidden p-2 ${onLight ? "text-forest-900" : "text-ivory"}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-ivory-400 bg-ivory" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-3">
            <Link to="/services/cost-segregation" onClick={() => setOpen(false)} className="text-charcoal py-2">Cost Segregation</Link>
            <Link to="/services/rd-tax-credit" onClick={() => setOpen(false)} className="text-charcoal py-2">R&D Tax Credit</Link>
            <Link to="/for-tax-professionals" onClick={() => setOpen(false)} className="text-charcoal py-2">For CPAs</Link>
            <Link to="/about" onClick={() => setOpen(false)} className="text-charcoal py-2">About</Link>
            <Link to="/knowledge" onClick={() => setOpen(false)} className="text-charcoal py-2">Knowledge Center</Link>
            <Link to="/knowledge?tab=faqs" onClick={() => setOpen(false)} className="text-charcoal py-2">FAQs</Link>
            <Link to="/calculators/cost-segregation" onClick={() => setOpen(false)} className="text-charcoal py-2">Cost Segregation Calculator</Link>
            <Link to="/calculators/rd-tax-credit" onClick={() => setOpen(false)} className="text-charcoal py-2">R&D Calculator</Link>
            <a href="/#contact" onClick={() => setOpen(false)} className="bg-brass text-forest-900 text-center py-3 mt-2 font-medium" data-testid="mobile-nav-cta-consult">
              Free Consultation
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
